import { AxiosInstance, AxiosResponse, InternalAxiosRequestConfig, isAxiosError } from "axios";
import { Session } from "PFApp/use_session";
import { parseResponseFilters } from "PFCore/api/helpers/api_filters_to_v2_parser";
import {
  isServerErrorResponse,
  isUnauthorisedErrorResponse
} from "PFCore/helpers/api_errors/api_error_responses";

import { RequestConfig } from "./api";
import { getInstance } from "./instance";
import {
  addPaginationParamsToResponse,
  cleanRequestParams,
  clearInterceptors,
  parseRequestToSnakeCase,
  parseResponseToCamelCase,
  refreshTokenAndRetry,
  setHeaders,
  setParamsSerializer
} from "./utils";

const configureRequests = (instance: AxiosInstance, session: Session, fullDomain?: string): void => {
  instance.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    const newConfig = { ...config };

    setHeaders(newConfig, session.getAccessToken(), fullDomain);

    if (newConfig.headers["Content-Type"] === "multipart/form-data") {
      return newConfig;
    }

    cleanRequestParams(newConfig);
    parseRequestToSnakeCase(newConfig);
    setParamsSerializer(newConfig);

    return newConfig;
  });
};

type QuotaExceededData = {
  featureName: string;
  resetsAt: string;
};

export const configureResponses = (
  instance: AxiosInstance,
  session: Session,
  onServerError?: VoidFunction,
  onQuotaExceeded?: (data: QuotaExceededData) => void
): void => {
  instance.interceptors.response.use(
    (response: AxiosResponse) => {
      if (!(response.config as RequestConfig).skipCamelization) {
        parseResponseToCamelCase(response);
      }

      addPaginationParamsToResponse(response);
      parseResponseFilters(response);

      return response;
    },
    (error) => {
      if (!isAxiosError(error)) {
        return Promise.reject(error);
      }

      error.response && parseResponseToCamelCase(error.response);
      const originalRequest = error.config;
      const isOauthTokenUrl = originalRequest?.url?.match("oauth/token");
      const isQuotaExceeded = error.response?.status === 429;

      if (isServerErrorResponse(error)) {
        onServerError?.();
      }

      if (isQuotaExceeded && error.response) {
        onQuotaExceeded?.(error.response.data as QuotaExceededData);
      }

      if (isUnauthorisedErrorResponse(error) && !isOauthTokenUrl) {
        return refreshTokenAndRetry(session, originalRequest);
      }

      return Promise.reject(error);
    }
  );
};

export const setupAxiosInstance = (
  session: Session,
  fullDomain?: string,
  onServerError?: VoidFunction,
  onQuotaExceeded?: (data: QuotaExceededData) => void
): void => {
  const instance = getInstance();
  clearInterceptors(instance);
  configureRequests(instance, session, fullDomain);
  configureResponses(instance, session, onServerError, onQuotaExceeded);
};
